import { createFileRoute, Link } from "@tanstack/react-router";

const TITLE = "FSH Levels Chart & Interpretation";
const DESCRIPTION =
  "FSH levels explained: a clear chart of ranges (mIU/mL) across premenopause, perimenopause, and postmenopause, plus how to read your lab results.";
const URL = "https://knowmeno.com/guide/fsh-levels";

export const Route = createFileRoute("/guide/fsh-levels")({
  head: () => ({
    meta: [
      { title: `${TITLE} — KnowMeno` },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          image: ["https://knowmeno.com/og-image.png?v=2"],
          datePublished: "2026-01-15",
          dateModified: "2026-06-29",
          inLanguage: "en",
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "KnowMeno" },
          publisher: {
            "@type": "Organization",
            name: "KnowMeno",
            logo: { "@type": "ImageObject", url: "https://knowmeno.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What FSH level confirms menopause?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A consistently elevated FSH above 30 mIU/mL — along with 12 consecutive months without a period — is the typical threshold used to confirm postmenopause. A single reading is not enough; FSH can fluctuate widely in perimenopause.",
              },
            },
            {
              "@type": "Question",
              name: "Can FSH levels be normal during perimenopause?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. FSH fluctuates dramatically during perimenopause and a single normal reading does not rule it out. Most clinicians diagnose perimenopause from symptoms and cycle changes, not from one lab value.",
              },
            },
            {
              "@type": "Question",
              name: "What is a normal FSH level by age?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Premenopausal women typically have FSH between 4.7–21.5 mIU/mL during the follicular phase. Postmenopausal ranges are generally 25.8–134.8 mIU/mL. Ranges vary by lab.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: FshLevelsGuide,
});

const PLUM = "#4B1F4F";
const PINK = "#FF5C7A";
const CREAM = "#FAF7F5";
const BORDER = "#E8D5DA";
const TEXT = "#2D1A2E";
const MUTED = "#6E4B71";

interface Row {
  stage: string;
  range: string;
  meaning: string;
  bg: string;
}

const ROWS: Row[] = [
  {
    stage: "Premenopausal (follicular phase)",
    range: "4.7 – 21.5 mIU/mL",
    meaning: "Typical reproductive-age baseline. Ovaries respond normally to FSH.",
    bg: "#FFFFFF",
  },
  {
    stage: "Premenopausal (mid-cycle peak)",
    range: "5.9 – 21.0 mIU/mL",
    meaning: "Short surge that triggers ovulation.",
    bg: "#FFFFFF",
  },
  {
    stage: "Premenopausal (luteal phase)",
    range: "1.7 – 7.7 mIU/mL",
    meaning: "Lower after ovulation while progesterone rises.",
    bg: "#FFFFFF",
  },
  {
    stage: "Early perimenopause",
    range: "10 – 25 mIU/mL (variable)",
    meaning: "Ovaries need a louder signal to ovulate. Cycles still mostly regular.",
    bg: "#FFF4EC",
  },
  {
    stage: "Late perimenopause",
    range: "25 – 40 mIU/mL (highly variable)",
    meaning: "Skipped periods, longer cycles, more intense symptoms. Single readings can still look normal.",
    bg: "#FFE7DB",
  },
  {
    stage: "Postmenopause",
    range: "> 30 mIU/mL (often 25.8 – 134.8)",
    meaning: "Sustained elevation. Combined with 12 months of no periods, this confirms menopause.",
    bg: "#FFE4EA",
  },
];

function FshLevelsGuide() {
  return (
    <div
      style={{
        background: CREAM,
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: TEXT,
        padding: "32px 16px 80px",
      }}
    >
      <article
        style={{
          maxWidth: 720,
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: 20,
          padding: "32px 24px",
          border: `0.5px solid ${BORDER}`,
        }}
      >
        <nav style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: "none" }}>
            KnowMeno
          </Link>{" "}
          / Guides / FSH Levels
        </nav>

        <h1
          style={{
            fontSize: 32,
            lineHeight: 1.15,
            color: PLUM,
            margin: "0 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          FSH Levels Menopause Chart & Interpretation
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, marginTop: 0 }}>
          A clear, plain-English guide to follicle-stimulating hormone (FSH) ranges across the
          menopause transition — and what your lab number actually means in context.
        </p>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            What is FSH?
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Follicle-stimulating hormone is released by the pituitary gland to tell the ovaries to
            mature an egg each cycle. As ovarian reserve declines in the lead-up to menopause, the
            ovaries respond less, so the pituitary releases <strong>more</strong> FSH to compensate.
            That rising FSH is one of the most consistent biological signatures of the menopause
            transition.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            FSH levels chart by menopause stage
          </h2>
          <div
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ background: PLUM, color: "#FFFFFF", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>Stage</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>FSH range</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>What it suggests</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.stage} style={{ background: r.bg, borderTop: `1px solid ${BORDER}` }}>
                    <td style={{ padding: "12px", fontWeight: 500, verticalAlign: "top" }}>
                      {r.stage}
                    </td>
                    <td style={{ padding: "12px", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {r.range}
                    </td>
                    <td style={{ padding: "12px", color: MUTED, lineHeight: 1.55 }}>{r.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.55 }}>
            Ranges are typical reference values and vary by laboratory. Always interpret your result
            against the reference range printed on your own lab report.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            What does FSH over 30 mIU/mL mean?
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            An FSH above <strong>30 mIU/mL</strong> on two readings several weeks apart, combined
            with at least 12 consecutive months without a period, is the textbook threshold for
            confirming postmenopause. On its own — without the cycle history — a single high FSH is
            <em> suggestive</em>, not diagnostic.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Why one FSH test isn't enough in perimenopause
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            During perimenopause, FSH swings dramatically from cycle to cycle — and even within a
            single cycle. You can have a "menopausal" reading one month and a normal reading the
            next. That's why most menopause societies (NAMS, the British Menopause Society) advise
            against diagnosing perimenopause from FSH alone in women over 45 with typical symptoms.
            Your symptom pattern and cycle changes are usually more informative than the lab value.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Things that can skew your FSH result
          </h2>
          <ul style={{ fontSize: 15, lineHeight: 1.8, paddingLeft: 20 }}>
            <li>
              <strong>Hormonal birth control</strong> suppresses FSH; readings while on the pill,
              ring, patch, or hormonal IUD aren't reliable indicators of menopause stage.
            </li>
            <li>
              <strong>HRT / MHT</strong> also lowers FSH — you can't use FSH to "check" menopause
              while on hormone therapy.
            </li>
            <li>
              <strong>Cycle day matters.</strong> In premenopausal women, day 2–4 of the cycle gives
              the most comparable result.
            </li>
            <li>
              <strong>PCOS, thyroid disease, and pituitary disorders</strong> can all shift FSH up
              or down independently of menopause.
            </li>
          </ul>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            How to use your FSH result with KnowMeno
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            A lab number is one data point. KnowMeno tracks the other half of the picture —
            symptoms, cycle changes, mood, sleep, HRT response — so your clinician sees an FSH
            value <strong>alongside</strong> three months of objective trends instead of a single
            snapshot.
          </p>
          <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link
              to="/sign-up"
              style={{
                background: PLUM,
                color: "#FFFFFF",
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Start tracking free for 7 days
            </Link>
            <Link
              to="/"
              style={{
                background: "transparent",
                color: PLUM,
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
                border: `1px solid ${PLUM}`,
              }}
            >
              How KnowMeno works
            </Link>
          </div>
        </section>

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            Frequently asked questions
          </h2>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              What FSH level confirms menopause?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              A consistently elevated FSH above 30 mIU/mL — along with 12 consecutive months
              without a period — is the typical threshold. A single reading is not enough.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Can FSH levels be normal during perimenopause?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Yes. FSH fluctuates dramatically during perimenopause and a single normal reading
              does not rule it out. Most clinicians diagnose perimenopause from symptoms and cycle
              changes, not one lab value.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              What is a normal FSH level by age?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Premenopausal women typically have FSH between 4.7–21.5 mIU/mL during the follicular
              phase. Postmenopausal ranges are generally 25.8–134.8 mIU/mL. Ranges vary by lab.
            </p>
          </details>

          <details
            style={{
              borderTop: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              padding: "12px 0",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Should I get an FSH test if I'm over 45 with typical symptoms?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Major menopause societies say no — symptoms and cycle history are enough. FSH testing
              is more useful under 45 (to investigate possible early menopause) or when symptoms
              are atypical.
            </p>
          </details>
        </section>

        <p
          style={{
            marginTop: 28,
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.6,
            fontStyle: "italic",
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 16,
          }}
        >
          This guide is for general education and is not medical advice. Always discuss your lab
          results with a qualified clinician who knows your full history.
        </p>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link
            to="/"
            style={{ color: PINK, fontSize: 14, textDecoration: "none", fontWeight: 500 }}
          >
            ← Back to KnowMeno
          </Link>
        </div>
      </article>
    </div>
  );
}
